export const mrpDe = {
  title: 'Materialbedarfsplanung',
  subtitle:
    'Zeitabhängige Bedarfe, Zugänge und unverbindliche Handlungsvorschläge mit reproduzierbarem Quellsnapshot.',
  help: { open: 'Hilfe zur Materialbedarfsplanung öffnen' },
  tabs: { planning: 'Planung', history: 'Laufhistorie', policies: 'Dispositionsparameter' },
  run: {
    title: 'Planungslauf erstellen',
    description:
      'Stichtag, Horizont und optionalen Artikelumfang wählen. Ein MRP-Lauf erzeugt niemals automatisch Aufträge.',
  },
  fields: {
    asOf: 'Planungsstichtag',
    horizon: 'Horizontende',
    productScope: 'Artikelumfang',
    strategy: 'Beschaffungsstrategie',
    safetyStock: 'Sicherheitsbestand',
    procurementLead: 'Beschaffungszeit (Tage)',
    productionLead: 'Produktionszeit (Tage)',
    productionMinimum: 'Mindestproduktionsmenge',
    productionLot: 'Produktionslosgröße',
    note: 'Grund / Notiz',
  },
  allProducts: 'Alle bestandsgeführten Artikel',
  actions: {
    run: 'MRP ausführen',
    'convert-purchase': 'Bestellentwurf erzeugen',
    'convert-production': 'Produktionsentwurf erzeugen',
    dismiss: 'Vorschlag verwerfen',
  },
  messages: {
    invalidInput: 'Bitte Planungsangaben prüfen.',
    runCreated: 'Der unveränderliche MRP-Snapshot wurde erstellt.',
    policySaved: 'Dispositionsparameter gespeichert.',
    'convert-purchase': 'Bestellentwurf wurde ausdrücklich erzeugt.',
    'convert-production': 'Produktionsauftragsentwurf wurde ausdrücklich erzeugt.',
    dismiss: 'Vorschlag wurde mit Begründung verworfen.',
  },
  stats: {
    products: 'Artikel',
    shortages: 'Fehlmengen',
    purchase: 'Bestellvorschläge',
    production: 'Produktionsvorschläge',
    manual: 'Manuelle Prüfung',
    warnings: 'Warnungen',
  },
  columns: {
    product: 'Artikel',
    gross: 'Bruttobedarf',
    available: 'Verfügbarer Bestand',
    reserved: 'Reserviert',
    receipts: 'Erwartete Zugänge',
    net: 'Nettobedarf',
    due: 'Bedarfstermin',
    action: 'Handlungsvorschlag',
    date: 'Datum',
    event: 'Ereignis',
    quantity: 'Menge',
    formula: 'Berechnung',
    run: 'Lauf',
    created: 'Erstellt',
  },
  empty: {
    noRunTitle: 'Kein Planungsergebnis gewählt',
    noRun: 'Neuen Lauf erstellen oder ein unveränderliches Ergebnis aus der Historie wählen.',
  },
  snapshot: 'Quellhash {{sourceHash}} · Ergebnishash {{resultHash}}',
  details: {
    timeline: 'Berechnungsnachweis',
    timelineDescription:
      'Am selben Tag werden Zugänge vor Bedarfen verarbeitet; jeder Planzugang stellt den Zielpuffer wieder her.',
    sources: 'Bedarfs- und Zugangsquellen',
    sourcesDescription:
      'Jede Menge verweist auf den Beleg, Produktionsauftrag oder Bestandssnapshot dieses Laufs.',
  },
  events: {
    OPENING: 'Anfangsbestand',
    DEMAND: 'Bedarf',
    SUPPLY: 'Zugang',
    PLANNED_RECEIPT: 'Vorgeschlagener Zugang',
  },
  sourceTypes: {
    SALES_ORDER: 'Kundenauftrag',
    PRODUCTION_MATERIAL: 'Geplantes Produktionsmaterial',
    PLANNED_PRODUCTION_EXPLOSION: 'Aufgelöster Rezepturbedarf',
    STOCK: 'Verwendbarer Bestandssnapshot',
    PURCHASE_ORDER: 'Offene Bestellung',
    PRODUCTION_ORDER: 'Geplanter Produktionszugang',
  },
  suggestion: { none: 'Keine Fehlmenge' },
  statuses: { OPEN: 'Offen', CONVERTED: 'Umgewandelt', DISMISSED: 'Verworfen' },
  strategies: {
    AUTO: 'Automatische Regel',
    BUY: 'Einkaufen',
    MAKE: 'Produzieren',
    BUY_OR_MAKE: 'Einkaufen oder produzieren',
    NONE: 'Nur manuell prüfen',
  },
  policy: {
    title: 'Artikelbezogene Dispositionsparameter',
    description:
      'Schwellenwerte bleiben im Artikelstamm; hier werden Strategie, Zeiten und Losgrößen ergänzt.',
    dialogTitle: 'Dispositionsparameter bearbeiten',
    dialogDescription: 'Versionsprüfungen verhindern paralleles Überschreiben.',
  },
  decision: {
    'convert-purchase': {
      title: 'Bestellentwurf erzeugen?',
      description:
        'Diese ausdrückliche Aktion erzeugt genau einen Entwurf aus dem eingefrorenen Vorschlag. Er wird weder versendet noch bestätigt.',
      confirm: 'Ich bestätige die Erzeugung des Bestellentwurfs.',
    },
    'convert-production': {
      title: 'Produktionsauftragsentwurf erzeugen?',
      description:
        'Diese ausdrückliche Aktion erzeugt einen Entwurf aus der im Vorschlag gespeicherten freigegebenen Rezeptur und dem Arbeitsplan.',
      confirm: 'Ich bestätige die Erzeugung des Produktionsauftragsentwurfs.',
    },
    dismiss: {
      title: 'Handlungsvorschlag verwerfen?',
      description:
        'Die Fehlmenge bleibt im unveränderlichen Lauf sichtbar; der Grund wird revisionssicher protokolliert.',
      confirm: 'Ich bestätige das Verwerfen dieses Vorschlags.',
    },
  },
  warningTitle: 'Planungswarnung',
  warnings: {
    MRP_BATCH_STOCK_RECONCILIATION: 'Die Summe verwendbarer Chargen weicht vom Artikelbestand ab.',
    MRP_BATCH_SHELF_LIFE_MISSING:
      'Mindestens eine Charge hat keine erforderlichen Haltbarkeitsdaten und wurde ausgeschlossen.',
    MRP_INCOMPATIBLE_UNIT:
      'Eine Quellmenge hat eine inkompatible Einheit und wurde ausgeschlossen.',
    MRP_SUPPLIER_MISSING: 'Keine aktive freigegebene Lieferantenzuordnung vorhanden.',
    MRP_APPROVED_RECIPE_OR_ROUTING_MISSING:
      'Keine passende freigegebene Rezeptur mit Arbeitsplan vorhanden.',
    MRP_PROCUREMENT_DISABLED: 'Automatische Beschaffung ist für diesen Artikel deaktiviert.',
    MRP_RELEASE_DATE_OVERDUE:
      'Der errechnete Freigabetermin liegt vor dem Stichtag; sofortige Prüfung erforderlich.',
  },
} as const;
